<template>
	<div class="order-row" :style="'--status-color: '+ordersGroup.statusColor+'; --payment-color: '+ordersGroup.paymentColor+'; --paid-color: '+(ordersGroup.paid ? '#62D18D' : '#E24B74')+'; --plugin-color: '+pluginColor" :class="{selected: ordersStore.selectedGroupId === ordersGroup.groupIdentifier}">
		<div class="first-row">
			<div class="titles">
				<div class="title">{{ orderTitles.title }}</div>
				<div class="subtitle" v-show="orderTitles.subtitle">
					<span>{{ orderTitles.subtitle }}</span>
				</div>
				<div class="subtitle withIcon" v-if="ordersGroup.order_mode === 'delivery'" @click.stop="clickDeliveryInfo">
					<div class="icon-info" v-html="svgStore.get('iconInfo')"></div>
					<span>{{ getString('other_info') }}</span>
				</div>
			</div>
			<div class="order-status">
				<div class="date">{{ orderDate ? orderDate : '' }}</div>
				<div class="order-state-container">
					<div class="order-state" v-if="!orderIsCompleted">
						<div class="status">{{ orderStatus }}</div>
					</div>
					<div class="order-state" v-else>
						<div class="status">{{ ordersGroup.paymentMethodName }}</div>
					</div>
					<div class="payment-container" v-if="!orderIsCompleted">
						<div class="payment-icon" :class="{pluginIcon}"><img :src="orderStatusIcon" alt="payment icon"></div>
					</div>
				</div>
			</div>
		</div>
		<div class="second-row">
			<div class="order-info">
				<div class="orders-number">{{ ordersNumber }}</div>
				<div class="orders-items">{{ ordersItems }}</div>
			</div>
			<div class="order-action">
				<div class="order-princing">
					<div v-if="ordersGroup.type === 'multi' && !orderIsCompleted && ordersTotal !== ordersPaid" class="orders-value-partial"><span>{{ getString('payed') }}</span> {{ ordersPaid }}</div>
					<div class="orders-value">{{ ordersTotal }}</div>
				</div>
				<div class="order-main-action" v-if="!orderIsCompleted">
					<AppButtonIcon :icon-name="ordersAction.iconName" :loading="loadingAction" @click.stop="ordersActionClicked" prevent-change :disabled="orderActionDisabled"/>
				</div>
			</div>
		</div>
		<div class="badges-container">
			<div v-if="ordersGroup.sms_sent" class="badge purple" v-html="svgStore.get('iconWhatsapp')"></div>
			<div v-if="isFiscalized" class="badge orange" v-html="svgStore.get('iconAde')"></div>
			<div v-if="isFiscalizedFailed" class="badge red" v-html="svgStore.get('iconAde')"></div>
			<div v-if="hasInvoice" class="badge orange" v-html="svgStore.get('iconInvoice')"></div>
			<div v-if="!orderIsCompleted && ordersGroup.alert" class="badge red small" v-html="svgStore.get('iconBell')"></div>
		</div>
		<div class="plugin-after" v-if="pluginColor"></div>
	</div>

</template>

<script>
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {formatDateMMDDHHmm, getMinutesPassedFromDate} from "@/shared/helpers/dates";
import {useCurrencyStore, useLanguageStore, useNotificationStore, useSvgStore} from "@/shared/stores";
import {useOrdersStore} from "@/console-new/stores/orders.store";
import {useFunnelStore, useNativeStore, useOrdersActionsStore, useUserStore} from "@/console-new/stores";
import {orderFiscalizedStatus} from "@/console-new/helpers/orders";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const ordersStore = useOrdersStore();
		const svgStore = useSvgStore();
		const notificationStore = useNotificationStore();
		const ordersActionsStore = useOrdersActionsStore();
		const nativeStore = useNativeStore();
		const funnelStore = useFunnelStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {
			getString: languageStore.getString,
			languageStore,
			ordersStore,
			svgStore,
			ordersActionsStore,
			nativeStore,
			funnelStore,
			currencyStore,
			userStore,
			notificationStore
		}
	},
	name: "OrderRow",
	components: {AppButtonIcon},
	props: {
		ordersGroup: {
			type: Object,
			required: true,
			validator: value => value.hasOwnProperty("orders") && value.orders.length > 0 && value.hasOwnProperty("status")
		},
	},
	data() {
		return {
			loadingAction: false,
			orderDate: null,
			dateInterval: null
		}
	},
	computed: {
		orderIsCompleted() {
			return this.ordersGroup.status === this.ordersStore.STATUS.completed || this.ordersGroup.status === this.ordersStore.STATUS.canceled || this.ordersGroup.status === this.ordersStore.STATUS.failed;
		},
		pluginIcon() {
			if(this.ordersGroup.paid && this.ordersGroup.type === "external" && this.ordersGroup.orders.length > 0 && this.ordersGroup.orders.every(order => !!order.plugin)) {
				let plugin = this.ordersGroup.orders[0].plugin;
				if(plugin.icon) {
					return plugin.icon;
				} else {
					return null;
				}
			}
			return null;
		},
		pluginColor() {
			if(this.ordersGroup.orders.length > 0 && this.ordersGroup.orders.every(order => !!order.plugin)) {
				let plugin = this.ordersGroup.orders[0].plugin;
				if(plugin.color) {
					return "#"+plugin.color;
				} else {
					return null;
				}
			}
			return null;
		},
		fiscalStatus() {
			if(this.ordersGroup && this.ordersGroup.orders) {
				let allFiscalized = true;
				for(let i = 0; i < this.ordersGroup.orders.length; i++) {
					let status = orderFiscalizedStatus(this.ordersGroup.orders[i]);
					if(status === "pending") {
						return "pending";
					} else if(status === "not_fiscalized") {
						allFiscalized = false;
						break;
					}
				}
				if(allFiscalized) {
					return "fiscalized";
				} else {
					return "not_fiscalized";
				}
			}
			return "not_fiscalized";
		},
		isFiscalized() {
			if(this.ordersGroup.type === "manual") {
				return false;
			}
			if(this.ordersGroup.type === "multi") {
				let arePaymentsAllManual = true;
				outer: for(let i = 0; i < this.ordersGroup.orders.length; i++) {
					for(let j = 0; j < this.ordersGroup.orders[i].payments.length; j++) {
						if(this.ordersGroup.orders[i].payments[j].type !== "manual") {
							arePaymentsAllManual = false;
							break outer;
						}
					}
				}
				if(arePaymentsAllManual) {
					return false;
				}
			}
			return this.fiscalStatus === "fiscalized" && this.ordersGroup.type !== "manual";
		},
		hasInvoice() {
			if(!this.ordersGroup?.orders?.length || !this.ordersGroup?.orders?.find) {
				return false;
			}
			return this.ordersGroup.orders.some(order =>
				order.invoice_id ||
				order?.payments?.length && order.payments.some(payment => payment.invoice_id)
			);
		},
		isFiscalizedFailed() {
			return this.fiscalStatus === "pending";
		},
		orderTitles() {
			return {
				title: this.ordersGroup.title,
				subtitle: this.ordersGroup.subtitle
			};
		},
		orderStatus() {
			return this.getString(this.ordersGroup.status);
		},
		orderStatusIcon() {
			if(this.ordersGroup.paid) {
				if(this.ordersGroup.type === "klarna") {
					return "https://img.qromo.io/img/icons/klarna-logo-green.png";
				} else if(this.ordersGroup.type === "btc") {
					return "https://img.qromo.io/img/icons/bitcoin-logo-green.png";
				} else if(this.ordersGroup.type === "satispay") {
					return "https://img.qromo.io/img/icons/satispay-logo-green.jpg";
				} else if(this.ordersGroup.type === "manual") {
					return "https://img.qromo.io/img/icons/icon-manual-green.svg";
				} else if(this.ordersGroup.type === "cash") {
					return "https://img.qromo.io/img/icons/icon-cash-already-paid.svg";
				} else if(this.ordersGroup.type === "stripe") {
					return "https://img.qromo.io/img/icons/icon-card-payed.svg";
				} else if(this.ordersGroup.type === "check") {
					return "https://img.qromo.io/img/icons/icon-check-already-paid.svg";
				} else if(this.ordersGroup.type === "ticket") {
					return "https://img.qromo.io/img/icons/icon-ticket-paid.svg";
				} else if(this.ordersGroup.type === "paylater") {
					return "https://img.qromo.io/img/icons/icon-paylater-paid-new.svg";
				} else if(this.ordersGroup.type === "customer_credit") {
					return "https://img.qromo.io/img/icons/icon-customer-credit.svg";
				} else if(this.ordersGroup.type === "wire") {
					return "https://img.qromo.io/img/icons/icon-wire-transfer-paid.svg";
				} else if(this.ordersGroup.type === "multi") {
					return "https://img.qromo.io/img/icons/icon-multipay-green-new.svg";
				} else if(this.ordersGroup.type === "external") {
					if(this.ordersGroup.orders.length > 0 && this.ordersGroup.orders.every(order => !!order.plugin)) {
						let plugin = this.ordersGroup.orders[0].plugin;
						if(plugin.icon) {
							return plugin.icon;
						} else {
							return "https://img.qromo.io/img/icons/plugin-icon-payment.svg";
						}
					} else {
						return "https://img.qromo.io/img/icons/icon-paid-new.svg";
					}
				} else {
					return "https://img.qromo.io/img/icons/icon-paid-new.svg";
				}
			} else {
				if(this.ordersGroup.type === "multi") {
					return "https://img.qromo.io/img/icons/icon-multipay-yellow-new.svg";
				} else {
					return "https://img.qromo.io/img/icons/icon-not-already-paid-new.svg";
				}
			}
		},
		ordersNumber() {
			if(this.ordersGroup.orders.length > 1) {
				return this.getString("orders_num", [this.ordersGroup.orders.length]);
			} else if(this.ordersGroup.orders.length === 1){
				return this.getString("order_num", [this.ordersGroup.orders[0].orderNumberDayFormatted]);
			} else {
				return "";
			}
		},
		ordersItems() {
			let items = 0;
			this.ordersGroup.orders.forEach(order => {
				items += order.items.reduce((acc, item) => acc + item.quantity, 0);
			});
			if(items > 1) {
				return this.getString("items_num", [items]);
			} else {
				return this.getString("item_num");
			}
		},
		ordersTotal() {
			let total = 0;
			this.ordersGroup.orders.forEach(order => {
				total += order.grandTotal;
			});
			return this.currencyStore.readableCurrencyValue(total);
		},
		ordersPaid() {
			if(this.ordersGroup.type !== "multi") {
				return null;
			}
			return this.currencyStore.readableCurrencyValue(this.ordersGroup.orders.reduce((paid, order) => {
				paid += order.paymentsPartial;
				return paid;
			}, 0));
		},
		ordersAction() {
			return this.ordersActionsStore.ordersGroupAction(this.ordersGroup);
		},
		orderActionDisabled() {
			return (this.ordersAction.actionId === 'payment' && !this.userStore.checkAnyOrderPayAllowed) ||
				(this.ordersAction.actionId === 'next' && (!this.userStore.checkOrderEditAllowed && !this.userStore.checkOrderCreationAllowed)) ||
				(this.ordersAction.actionId === 'complete' && !this.userStore.checkAnyOrderPayAllowed && !this.userStore.checkOrderCreationAllowed);
		}
	},
	methods: {
		clickDeliveryInfo() {
			if(this.ordersGroup.order_mode === 'delivery' && this.ordersGroup.orders.length === 1) {
				let order = this.ordersGroup.orders[0];
				this.ordersStore.deliveryOrderInfo = {
					customer_name: order.user_name,
					customer_phone: order.user_phone,
					address: order.title,
					address_details: order.address ? order.address.details : null,
					delivery_date: order.delivery_scheduled_date ? order.subtitle : null,
					tracking_url: order.rate ? order.rate.tracking_url : null,
					label_url: order.rate ? order.rate.label_url : null,
					rate: order.rate,
					orderNumberDayFormatted: order.orderNumberDayFormatted
				}
			}
		},
		ordersActionClicked() {

			if(this.nativeStore.isAllowActions) {
				this.loadingAction = true;
				this.ordersActionsStore.performAction(this.ordersAction.actionId, this.ordersGroup, () => {
					this.loadingAction = false;
				});
			} else {
				this.notificationStore.showNotification("permission-denied");
			}
		},
		refreshOrderDate() {
			if(this.orderIsCompleted) {
				return this.orderDate ? this.orderDate : formatDateMMDDHHmm(this.ordersGroup.date);
			} else {
				return getMinutesPassedFromDate(this.ordersGroup.date, true);
			}
		},
	},
	mounted() {
		this.orderDate = this.refreshOrderDate();
		this.dateInterval = setInterval(() => {
			this.orderDate = this.refreshOrderDate();
		}, 30000);
	},
	beforeUnmount() {
		clearInterval(this.dateInterval);
	},
}
</script>

<style lang="scss" scoped>
.order-row {
	--order-h-padding: 14px;

	background: var(--color-bg);
	box-shadow: 0 3px 12px var(--color-box-shadow-2);
	border-radius: 13px;
	padding: var(--order-h-padding) 18px var(--order-h-padding) 18px;
	position: relative;
	width: 100%;
	cursor: pointer;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	border: 1px solid transparent;
	transition: border 0.3s ease, box-shadow 0.3s ease;

	@media (max-width: 500px) {
		--order-h-padding: 8px;
		padding: var(--order-h-padding) 12px var(--order-h-padding) 12px;
	}

	&.selected {
		box-shadow: 0 0 0 var(--color-box-shadow-3);
		border: 1px solid var(--status-color);
	}

	* {
		pointer-events: none;
		user-select: none;
	}

	.first-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-start;

		.titles {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			flex-grow: 1;
			overflow: hidden;

			.title {
				font-size: 18px;
				line-height: 21px;
				color: var(--status-color);
				font-weight: 700;
				user-select: text;
				@include truncateString(2);
			}

			.subtitle {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-start;
				margin-top: 2px;
				max-width: 100%;

				&.withIcon {
					margin-top: 4px;
					border: 1px solid var(--status-color);
					border-radius: 13px;
					padding: 4px 8px 4px 4px;
					transition: background-color 0.3s ease;
					pointer-events: all;
					cursor: pointer;

					&:hover {
						background-color: var(--color-bg-primary-2);
					}
				}

				.icon-info {
					width: 16px;
					height: 16px;
					margin-right: 5px;
					flex-shrink: 0;
					background-color: var(--color-bg);
					border-radius: 13px;

					&:deep(svg) {
						width: 100%;
						height: 100%;

						.fill {
							fill: var(--status-color);
						}

						.stroke {
							stroke: var(--status-color);
						}
					}

				}

				span {
					font-size: 14px;
					line-height: 16px;
					color: var(--status-color);
					user-select: text;
					padding-right: 4px;
					width: 100%;
					@include truncateString(1);
				}
			}
		}

		.order-status {
			display: flex;
			flex-direction: column;
			align-items: flex-end;
			justify-content: flex-start;
			flex-shrink: 0;

			.date {
				font-size: 14px;
				line-height: 15px;
				color: #B2B2B2;
				text-align: right;
				@include truncateString(2);
				margin-left: 10px;
				min-width: 50px;
				margin-bottom: 6px;
			}

			.order-state-container {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: flex-end;
				margin-left: 4px;
				max-width: 100%;

				.order-state {
					overflow: hidden;

					.status {
						font-size: 14px;
						font-weight: 700;
						color: var(--status-color);
						@include truncateString();
					}

				}

				.payment-container {
					display: flex;
					flex-direction: row;
					align-items: center;
					margin-left: 4px;
					border-radius: 30px;

					.payment-icon {
						height: 28px;
						width: 28px;
						border-radius: 13px;
						display: flex;
						justify-content: center;
						align-items: center;

						&.pluginIcon {
							background-color: #62d18d;
							padding: 4px;
						}

						img {
							height: 100%;
							border-radius: 30px;
						}
					}
				}

			}

		}
	}

	.second-row {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: flex-end;
		margin-top: 5px;

		.order-info {
			display: flex;
			flex-direction: column;

			.orders-number {
				font-size: 14px;
				color: var(--color-txt);
				margin-bottom: 1px;
			}

			.orders-items {
				font-size: 14px;
				line-height: 16px;
				color: #B2B2B2;
			}
		}

		.order-action {
			display: flex;
			flex-direction: row;
			align-items: flex-end;

			.order-princing {
				display: flex;
				flex-direction: column;
				align-items: flex-end;
				justify-content: flex-end;

				.orders-value {
					font-size: 18px;
					color: var(--color-txt);
					font-weight: 700;
				}

				.orders-value-partial {
					font-size: 13px;
					color: #B2B2B2;
					font-weight: 700;

					span {
						font-size: 12px;
						color: #B2B2B2;
						font-weight: normal;
					}
				}

			}

			.order-main-action {
				margin-left: 12px;

				@media (max-width: 500px) {
					margin-left: 8px;
				}

				.icon-button {
					cursor: pointer;
					pointer-events: all;
					width: 36px;
					height: 36px;
				}
			}
		}
	}

	&::before {
		content: "";
		position: absolute;
		top: var(--order-h-padding);
		height: calc(100% - var(--order-h-padding) - var(--order-h-padding));
		left: -2px;
		width: 4px;
		border-radius: 13px;
		background: var(--status-color);
		z-index: 3;
	}

	.plugin-after {
		content: "";
		position: absolute;
		top: 0;
		height: 100%;
		right: 0;
		width: 12px;
		border: 1px solid var(--plugin-color);
		border-radius: 0 12px 12px 0;
		background: var(--plugin-color);
		z-index: 3;

		@media (max-width: 500px) {
			top: var(--order-h-padding);
			height: calc(100% - var(--order-h-padding) - var(--order-h-padding));
			border-radius: 13px;
			width: 4px;
			right: -2px;
		}
	}

	.badges-container {
		position: absolute;
		top: 18px;
		right: -9px;
		z-index: 4;
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		justify-content: flex-start;
		width: 17px;
		height: calc(100% - 36px);

		.badge {
			width: 17px;
			height: 17px;
			margin-bottom: 5px;
			border-radius: 50%;
			background-color: $primary;
			display: flex;
			align-items: center;
			justify-content: center;

			:deep(svg) {
				width: 60%;
				height: 60%;

				.fill {
					fill: var(--color-bg);
				}

				.stroke {
					stroke: var(--color-bg);
				}
			}

			&.purple {
				background-color: #8F4DFE;
			}

			&.red {
				background-color: #E24B74;
			}

			&.orange {
				background-color: #fd831d;
			}

			&.small {
				:deep(svg) {
					width: 50%;
					height: 50%;
				}
			}
		}
	}
}
</style>